import { ContractParty } from './../../pages/contracts/contract-form/contract-form.component';
import { ChangeDetectionStrategy, Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  DxAutocompleteModule,
  DxButtonModule,
  DxDataGridModule,
  DxDropDownBoxModule,
  DxTagBoxModule,
  DxValidationGroupModule,
  DxValidatorModule,
} from 'devextreme-angular';
import { AppGlobals } from '../../app.globals';
import { FeatureFlags } from '../../interfaces/enums/feature-flag';
import { ContactService } from '../../services/contact.service';
import { ConfigService } from '../../services/config.service';
import { SocietyContact, SocietyService } from '../../services/society.service';
import { Subscription } from 'rxjs';
import {
  ContactSearchService,
  ContactSearchSession,
  ContactSearchType,
  contactAddressLine,
  contactSearchLoadingText,
  contactSearchMessage,
  HighlightSegment,
  highlightSegments,
} from '../../services/contact-search.service';

import { SearchableDropdownDirective } from '../../directives/searchable-dropdown.directive';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';
@Component({
    selector: 'app-contacts-dropdown-box',
    imports: [
        CommonModule,
        DxDropDownBoxModule,
        DxDataGridModule,
        DxButtonModule,
        DxTagBoxModule,
        DxValidationGroupModule,
        DxValidatorModule,
        SearchableDropdownDirective,
        SvgIconComponent,
    ],
    templateUrl: './contacts-dropdown-box.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./contacts-dropdown-box.component.css']
})
export class ContactsDropdownBoxComponent implements OnInit, OnDestroy {
  @ViewChild('companyGrid') companyGrid;
  @ViewChild('personGrid') personGrid;
  @ViewChild('societyGrid') societyGrid;
  @ViewChild('contactDropDownBox') contactDropDownBox;

  @Input() contactId: Number;

  @Input() contactType: string;
  @Input() tagFilter: boolean = false;

  @Input() editMode: boolean;

  @Output() onContactSelected = new EventEmitter();
  @Output() onNewContactRequested = new EventEmitter<string>();

  @Input() persons;
  @Input() companies;
  /** Third column "Gesellschaften" (contactType 'society'), loaded here; off where only companies fit. */
  @Input() allowSocieties: boolean = true;
  /** A Gesellschaft can't be its own shareholder: left out of the list. */
  @Input() excludeSocietyId: number | null = null;
  private allSocieties: SocietyContact[] | null = null;
  private societiesSubscription: Subscription | null = null;

  get societies(): SocietyContact[] | null {
    if (!this.allowSocieties || !this.allSocieties || !AppGlobals.UserCan(FeatureFlags.Gesellschaften)) return null;
    return this.excludeSocietyId ? this.allSocieties.filter(s => s.id !== this.excludeSocietyId) : this.allSocieties;
  }

  /** Room for three columns, never wider than the window (16px margin each side). */
  readonly threeColumnMinWidth = () => Math.min(600, window.innerWidth - 32);

  /** The Gesellschaften there were when "Gesellschaft erstellen" opened its tab; the new one is picked on return. */
  private pendingSocietyIds: Set<number> | null = null;
  @Input() validationGroup;
  @Input() validationRequired: boolean = false;
  /** Defaults to the Partner wording every existing caller already shows. */
  @Input() validationMessage: string = $localize`:@@partnerIsRequired:Partner is required`;

  @Output() contactNameChanged: EventEmitter<any> = new EventEmitter<any>();

  gridBoxValue: string;
  isGridBoxOpened: boolean = false;

  searchTimer: any;
  ddbInstance: any;

  filter: any;

  tagFilterValues: any = []; // Initialize as empty array

  @Input() readOnly: boolean = false;
  @Input() placeholder: string = $localize`:@@contactSearchPlaceholder:Search name or e-mail …`;

  /** Lens on the left of the field; the clear and dropdown buttons stay where they were. */
  readonly searchIconOptions = {
    icon: 'gpd-search', // <i class="dx-icon dx-icon-gpd-search">, drawn in the CSS
    stylingMode: 'text',
    focusStateEnabled: false,
    hoverStateEnabled: false,
    activeStateEnabled: false,
    tabIndex: -1,
    elementAttr: { class: 'contact-search-icon', 'aria-hidden': 'true' },
  };

  /**
   * Persons, companies and Gesellschaften are searched on the server from 2 characters
   * (umlauts, sound-alikes, typos, titles, legal forms, e-mail, city). While it has an answer
   * every column shows exactly its hits in the server's order.
   */
  search: ContactSearchSession;
  readonly personRank = (row: any) => this.search?.rankOf('person', row?.id) ?? 0;
  readonly companyRank = (row: any) => this.search?.rankOf('company', row?.id) ?? 0;
  readonly societyRank = (row: any) => this.search?.rankOf('society', row?.id) ?? 0;
  readonly addressLine = contactAddressLine;

  /**
   * A message about the whole search ("Keine Treffer für …", the hint to type a name, an
   * error): shown once across the panel instead of squeezed into every column.
   */
  get searchMessage(): string | null {
    const search = this.search;
    if (this.searching || !search?.active || (search.status === 'ok' && search.items.length > 0)) return null;
    return contactSearchMessage(search);
  }

  /** A search is on its way: each column shows placeholder rows until the hits arrive. */
  get searching(): boolean {
    return !!this.search?.pending;
  }

  readonly searchingText = contactSearchLoadingText();

  /** A column without hits while another one has some just says so; empty while the panel shows the message. */
  noDataText(type: ContactSearchType): string {
    if (this.searching || this.searchMessage) return '';
    return contactSearchMessage(this.search, type) ?? $localize`:@@noData:No data`;
  }

  /**
   * The name in parts, the typed text highlighted while server hits are shown. Cached per query,
   * since the grid asks again on every render.
   */
  nameSegments(name: string): HighlightSegment[] {
    const query = this.search?.active ? this.search.query : '';
    if (query !== this.highlightQuery) {
      this.highlightQuery = query;
      this.highlightCache.clear();
    }
    let segments = this.highlightCache.get(name);
    if (!segments) {
      segments = highlightSegments(name, query);
      this.highlightCache.set(name, segments);
    }
    return segments;
  }

  private highlightQuery = '';
  private highlightCache = new Map<string, HighlightSegment[]>();

  /** Name as the list shows it: with title, like the search result ("Dr. Katharina Becker"). */
  personName(row: any): string {
    const name = row?.displayName ?? '';
    return row?.title && !name.startsWith(row.title) ? `${row.title} ${name}` : name;
  }

  contactTags: any = [];

  @Input() context: any;
  @Input() contractDuplicateValidation: boolean = false;
  /** Which contract party this field holds, so the duplicate message can name the other one. */
  @Input() partnerRole: 'intern' | 'extern';

  get duplicateMessage(): string {
    if (this.partnerRole === 'extern') {
      return $localize`:@@contactAlreadyInternalPartner:This contact is already the internal contract partner.`;
    }
    if (this.partnerRole === 'intern') {
      return $localize`:@@contactAlreadyExternalPartner:This contact is already the external contract partner.`;
    }
    return $localize`:@@partnerAlreadyExists:Internal and external contract partner can't be the same contact.`;
  }

  /** Shown inline below the field instead of DevExtreme's focus-only tooltip. */
  errorText: string | null = null;
  private validator: any;

  adapterConfig: {
    applyValidationResults?: Function | undefined;
    bypass?: Function | undefined;
    focus?: Function | undefined;
    getValue?: Function | undefined;
    reset?: Function | undefined;
    validationRequestsCallbacks?: Function[] | undefined;
  };

  constructor(
    private contactService: ContactService,
    private configService: ConfigService,
    private societyService: SocietyService,
    private appGlobals: AppGlobals,
    private contactSearchService: ContactSearchService
  ) {
    this.adapterConfig = {
      getValue: () => {
        let contactData = this.contactId;
        return contactData;
      },
      applyValidationResults: (e: any) => {
        if (!e.isValid) {
          return e;
        }
      },
    };

    this.validateContractContactDuplicate = this.validateContractContactDuplicate.bind(this);
  }

  async ngOnInit(): Promise<void> {
    // Here, not in the constructor: allowSocieties is only bound now. Hidden Gesellschaft hits
    // would take slots of the 50 (the maximum), which the up to three columns share.
    const searchSocieties = this.allowSocieties && AppGlobals.UserCan(FeatureFlags.Gesellschaften);
    const types: ContactSearchType[] = searchSocieties ? ['person', 'company', 'society'] : ['person', 'company'];
    this.search = this.contactSearchService.createSession(types, () => this.applyCombinedFilters(), 50);
    if(this.tagFilter){
      this.getContactTags();
    }
    if (this.allowSocieties) {
      this.societiesSubscription = this.societyService.contacts$.subscribe(list => (this.allSocieties = list));
      this.societyService.loadContacts();
    }
  }

  ngOnDestroy(): void {
    this.societiesSubscription?.unsubscribe();
    this.search?.dispose();
    clearTimeout(this.searchTimer);
  }

  // Back from the "Gesellschaft erstellen" tab: reload and pick the one created there.
  @HostListener('window:focus')
  async onWindowFocus(): Promise<void> {
    const known = this.pendingSocietyIds;
    if (!known) return;
    const list = await this.societyService.loadContacts(true);
    const created = list.filter(s => !known.has(s.id));
    if (created.length !== 1) return; // nothing saved yet (or unclear which): keep waiting
    this.pendingSocietyIds = null;
    if (!this.contactId) this.handleContactSelected({ data: created[0] }, 'society');
  }

  getContactTags(): void {
    this.configService.getContactTags({}).then(
      (result) => {
        if (result.hasError === true)
          AppGlobals.ShowErrorMessage(result.feedback);
        else {
          this.contactTags = result.contactTags;
        }
      },
      (error: any) => {
        AppGlobals.ShowErrorMessage(error);
      }
    );
  }

  companyGridOnInitialized(e: any): void {
    this.companyGrid = e.component;
    setTimeout(() => {
      this.filter = this.contactDropDownBox.instance.option('text');
      this.applyCombinedFilters();
    }, 1);
  }

  personGridOnInitialized(e: any): void {
    this.personGrid = e.component;
    setTimeout(() => {
      this.filter = this.contactDropDownBox.instance.option('text');
      this.applyCombinedFilters();
    }, 1);
  }

  societyGridOnInitialized(e: any): void {
    this.societyGrid = e.component;
    setTimeout(() => {
      this.filter = this.contactDropDownBox.instance.option('text');
      this.applyCombinedFilters();
    }, 1);
  }

  dropDownBoxValueChanged(e): void {
    clearTimeout(this.searchTimer);
    this.filter = this.contactDropDownBox.instance.option('text');
    if (!e.value) {
      this.contactId = null;
      this.contactType = null;
      this.onContactSelected.emit(null);
      this.search.setQuery('');
    }
  }

  newContactRequested(type: string): void {
    this.isGridBoxOpened = false;
    // A Gesellschaft has its own page: opened in a new tab, so the caller's form stays as it is.
    if (type === 'society') {
      this.pendingSocietyIds = new Set((this.allSocieties ?? []).map(s => s.id));
      window.open('contacts/addSociety', '_blank');
      return;
    }
    this.onNewContactRequested.emit(type);
  }

  handleContactSelected(e, type): void {
    this.contactId = e.data.id || null;
    this.contactType = type;
    this.onContactSelected.emit({ data: e.data, type: type });
    this.isGridBoxOpened = false;
  }

  dataGridOnContentReady(e: any): void {
    setTimeout(() => {
      this.ddbInstance.focus();
    }, 1);
  }

  onInput(e: any) {
    this.filter = this.contactDropDownBox.instance.option('text');
    this.search.setQuery(this.filter);
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => {
      if (this.isGridBoxOpened && this.isSearchIncomplete(e.component)) {
        this.applyCombinedFilters();
      } else {
        this.isGridBoxOpened = true;
      }
    }, 300);
  }

  isSearchIncomplete(dropDownBox) {
    let displayValue = dropDownBox.option('displayValue'),
      text = dropDownBox.option('text');
    text = text && text.length && text;
    displayValue = displayValue && displayValue.length && displayValue[0];
    return text !== displayValue;
  }

  onDropDownBoxInit(e): void {
    this.ddbInstance = e.component;
  }

  /** Tag filter as a function, for a column while it shows server hits. */
  private matchesTagFilter(item: any): boolean {
    if (!this.tagFilterValues?.length) return true;
    return this.tagFilterValues.some(tagId => (item?.tags ?? []).includes(tagId));
  }

  /**
   * A column's filter: the server's hits while a search is active (no second "contains" here,
   * that would throw away the sound-alikes and typos), sorted by the hidden rank column;
   * while a search is still on its way, left as it is; otherwise the "contains" filter as
   * before (short input, or the selected contact's name when the box opens).
   */
  private applyColumnFilter(grid: any, type: ContactSearchType, combinedFilter: any): void {
    const instance = grid?.instance;
    if (!instance || this.search.pending) return;
    if (this.search.active) {
      instance.columnOption('searchRank', { sortOrder: 'asc', sortIndex: 0 });
      instance.filter((item: any) => this.search.isHit(type, item?.id) && this.matchesTagFilter(item));
    } else {
      instance.columnOption('searchRank', 'sortOrder', undefined);
      instance.filter(combinedFilter);
    }
    instance.refresh();
  }

  // New method to combine text and tag filters
  private buildCombinedFilter(): any {
    let filters = [];

    // Add text filter if exists
    if (this.filter && this.filter.trim()) {
      filters.push(['displayName', 'contains', this.filter]);
    }

    // Add tag filters if exist
    if (this.tagFilterValues && this.tagFilterValues.length > 0) {
      // Create OR condition for multiple tags
      let tagFilters = this.tagFilterValues.map(tagId => ['tags', 'contains', tagId]);

      if (tagFilters.length === 1) {
        filters.push(tagFilters[0]);
      } else if (tagFilters.length > 1) {
        // For multiple tags, create OR condition
        let orCondition = [tagFilters[0]];
        for (let i = 1; i < tagFilters.length; i++) {
          orCondition.push('or', tagFilters[i]);
        }
        filters.push(orCondition);
      }
    }

    // Combine all filters with AND
    if (filters.length === 0) {
      return null; // No filters
    } else if (filters.length === 1) {
      return filters[0];
    } else {
      // Multiple filters, combine with AND
      let combinedFilter = [filters[0]];
      for (let i = 1; i < filters.length; i++) {
        combinedFilter.push('and', filters[i]);
      }
      return combinedFilter;
    }
  }

  // Updated method to apply combined filters
  applyCombinedFilters() {
    const combinedFilter = this.buildCombinedFilter();

    this.applyColumnFilter(this.companyGrid, 'company', combinedFilter);
    this.applyColumnFilter(this.personGrid, 'person', combinedFilter);
    this.applyColumnFilter(this.societyGrid, 'society', combinedFilter);
  }

  // Keep the old method name for backward compatibility, but make it use the new logic
  applyFilters() {
    this.applyCombinedFilters();
  }

  async filterContactsByTag(e: any) {
    // The tag values are automatically updated by the tag box
    // Just apply the combined filters
    this.applyCombinedFilters();
  }

  onValidatorInitialized(e: any) {
    this.validator = e.component;
  }

  onValidated(e: any) {
    this.errorText = e.isValid ? null : e.brokenRule?.message ?? null;
  }

  /** Re-runs validation only while an error is shown, so a fixed duplicate clears on both
   *  fields without flagging an untouched empty field as required. */
  revalidateIfInvalid() {
    if (this.errorText && this.validator) {
      this.validator.validate();
    }
  }

  validateContractContactDuplicate(e) {
    try {
      if (
        this.context.contractPartyIntern.contactId &&
        this.context.contractPartyIntern.contactId == this.context.contractPartyExtern.contactId &&
        this.context.contractPartyIntern.contactType == this.context.contractPartyExtern.contactType
      ) {
        return false;
      } else {
        return true;
      }
    } catch (error) {
      return false;
    }
  }
}
